import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { FormSection, MultiSelectPicker, AvatarCircle } from '@/components';
import { ITaskFormValues, ITaskAssignee } from '@/types/admin/tasks';
import { useGetAvailableAssigneesQuery } from '@/store/api/admin/tasksApi';
import { useAdminUser } from '@/hooks/admin/useAdminUser';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useResponsive } from '@/hooks/common/useResponsive';
import { DesktopMultiSelectDropdown } from '@/components/common/admin/desktop/DesktopMultiSelectDropdown';

interface TaskAssigneesSectionProps {
  formData: ITaskFormValues;
  errors: Record<string, string | undefined>;
  onFieldChange: (field: keyof ITaskFormValues, value: any) => void;
}

export const TaskAssigneesSection: React.FC<TaskAssigneesSectionProps> = ({
  formData,
  errors,
  onFieldChange,
}) => {
  const { layoutMode } = useResponsive();
  const adminUser = useAdminUser();
  const selectedPropertyId = adminUser.selectedPropertyId;

  const { data: availableAssignees = [], isLoading } = useGetAvailableAssigneesQuery(
    { propertyId: selectedPropertyId! },
    { skip: !selectedPropertyId }
  );

  // Convert assignees to options for MultiSelectPicker
  const assigneeOptions = useMemo(() => {
    return availableAssignees.map(assignee => ({
      label: `${assignee.firstName} ${assignee.lastName}`,
      value: assignee.id.toString(),
    }));
  }, [availableAssignees]);

  // Convert selected assignees to string array for MultiSelectPicker
  const selectedAssigneeIds = useMemo(() => {
    return formData.assignees.map(assignee => assignee.id!.toString());
  }, [formData.assignees]);

  // Handle assignee selection change
  const handleAssigneeChange = (selectedIds: string[]) => {
    const selectedAssignees = availableAssignees.filter(assignee => 
      selectedIds.includes(assignee.id.toString())
    );
    onFieldChange('assignees', selectedAssignees);
  };

  return (
    <FormSection title="Task Assignees" subtitle="Select who will be responsible for this task">
      <View style={styles.fieldContainer}>
        {layoutMode === 'desktop' ? (
          <DesktopMultiSelectDropdown
            label="Assignees"
            options={assigneeOptions}
            selectedValues={selectedAssigneeIds}
            onChange={handleAssigneeChange}
            placeholder="Select assignees"
            error={errors.assignees}
          />
        ) : (
          <MultiSelectPicker
            label="Assignees"
            options={assigneeOptions}
            selectedValues={selectedAssigneeIds}
            onSelectionChange={handleAssigneeChange}
            placeholder="Select assignees"
            error={errors.assignees}
          />
        )}
      </View>

      {formData.assignees.length > 0 && (
        <View style={styles.selectedAssignees}>
          <Text style={styles.selectedTitle}>Selected Assignees ({formData.assignees.length})</Text>
          <View style={styles.assigneesList}>
            {formData.assignees.map((assignee) => (
              <View key={assignee.id} style={styles.selectedAssigneeCard}>
                <AvatarCircle
                  firstName={assignee.firstName}
                  lastName={assignee.lastName}
                  size={32}
                />
                <Text style={styles.selectedAssigneeName}>
                  {assignee.firstName} {assignee.lastName}
                </Text>
              </View>
            ))}
          </View>
        </View>
      )}
    </FormSection>
  );
};

const styles = StyleSheet.create({
  fieldContainer: {
    marginBottom: 16,
  },
  selectedAssignees: {
    marginTop: 16,
  },
  selectedTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 12,
  },
  assigneesList: {
    gap: 8,
  },
  selectedAssigneeCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 8,
  },
  selectedAssigneeName: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_PRIMARY,
    flex: 1,
  },
});
